/**
 * CORE-1693 — репост в свою ленту: что репостится, как публикуется и что
 * сервер ответил. Без React и RN — общий для веб-ленты и нативной, проверяется
 * под jest.
 *
 * Контракт — BE CORE-1709: `POST /api/master/content/posts` (мастер) и
 * `POST /api/user/circle/posts` (пользователь) принимают `RepostOf.Kind`,
 * `RepostOf.Id`, для события `RepostOf.Source` = master | live; `Content` —
 * собственный комментарий, может быть пустым. Репост не создаёт второго
 * события, практики или записи: он ссылается на оригинал.
 */
import { DeviceEventEmitter } from 'react-native';
import { api } from './api';
import { newIdempotencyKey } from './idempotencyKey';
import type {
  MasterPost,
  RepostEventSource,
  RepostKind,
  RepostSource,
  RepostTarget,
  RoomResponse,
} from '../types/auth';
import { resolveRoomAuthor } from '../utils/roomAuthor';

/** Событие для лент: в свою ленту добавилась публикация (репост). */
export const FEED_POST_CREATED_EVENT = 'feed_post_created';

const KINDS: readonly string[] = ['post', 'announcement', 'event', 'practice', 'recording', 'room'];

/** Сузить строку сервера до известного вида; незнакомое — null. */
export function toRepostKind(raw: unknown): RepostKind | null {
  return typeof raw === 'string' && KINDS.includes(raw) ? (raw as RepostKind) : null;
}

/** Публикация — репост (с живым оригиналом или уже без него). */
export function isRepost(post: Pick<MasterPost, 'repostKind' | 'repostOf' | 'repostSourceUnavailable'>): boolean {
  return !!post.repostKind || !!post.repostOf || !!post.repostSourceUnavailable;
}

/**
 * Что репостится, когда в ленте нажали «Репост» на публикации.
 *
 * Анонс эфира, события или практики — это тоже публикация, но у сервера для
 * него отдельный вид: карточка оригинала тогда несёт ссылку на сущность
 * (`liveId` / `masterEventId` / `videoPracticeId` / `watchPartyId`), а не
 * только текст.
 */
export function repostTargetForPost(post: MasterPost): RepostTarget {
  const isAnnouncement =
    post.liveId != null ||
    post.masterEventId != null ||
    post.videoPracticeId != null ||
    post.watchPartyId != null ||
    (typeof post.type === 'string' && post.type.toLowerCase().includes('announcement'));
  return { kind: isAnnouncement ? 'announcement' : 'post', id: post.id };
}

export function repostTargetForEvent(id: number, source: RepostEventSource): RepostTarget {
  return { kind: 'event', id, source };
}

export function repostTargetForPractice(id: number): RepostTarget {
  return { kind: 'practice', id };
}

/** Запись эфира: id — тот же, что у самого эфира. */
export function repostTargetForRecording(liveId: number): RepostTarget {
  return { kind: 'recording', id: liveId };
}

/** CORE-1844 / CORE-1865 — сама Live-сессия (комната): сервер ссылается на неё по id. */
export function repostTargetForRoom(roomId: number): RepostTarget {
  return { kind: 'room', id: roomId };
}

/**
 * CORE-1865 — Live-сессия из календаря: её анонс, когда он есть
 * (`announcementPostId`, CORE-1844), иначе сама сессия. Репост не создаёт
 * второй сессии и не записывает никого на неё.
 */
export function repostTargetForLiveRoom(room: { id: number; announcementPostId?: number | null }): RepostTarget {
  return room.announcementPostId != null
    ? { kind: 'announcement', id: room.announcementPostId }
    : repostTargetForRoom(room.id);
}

type RoomForRepost = Pick<
  RoomResponse,
  'id' | 'title' | 'coverImageUrl' | 'videoPracticeTitle' | 'videoPracticeCoverImageUrl' | 'status' | 'endedAtUtc'
  | 'scheduledAtUtc' | 'startedAtUtc' | 'announcementPostId' | 'visibility' | 'isPublic'
  | 'hostUserId' | 'hostName' | 'hostAvatarUrl' | 'creatorUserId' | 'creatorName' | 'creatorAvatarUrl'
>;

/** Приватную сессию сервер не репостит (CORE-1844); правило то же, что в календаре (CORE-1865). */
export function isPrivateRoom(room: Pick<RoomResponse, 'visibility' | 'isPublic'>): boolean {
  return room.visibility === 'Private' || (room.visibility == null && room.isPublic === false);
}

/** CORE-1872 — почему у Live-сессии нет репоста: приватная или отменённая встреча. */
export type RoomRepostBlock = 'private' | 'cancelled';

/**
 * CORE-1872 — отменённая встреча не «прошедшее мероприятие»: её не распространяют.
 * Приватную сессию сервер не репостит вовсе (CORE-1844).
 */
export function roomRepostBlock(room: Pick<RoomResponse, 'visibility' | 'isPublic' | 'status'>): RoomRepostBlock | null {
  if (isPrivateRoom(room)) return 'private';
  if (room.status === 'Cancelled') return 'cancelled';
  return null;
}

/** Сессия прошла: завершена или отменена. */
export function isRoomOver(room: Pick<RoomResponse, 'status' | 'endedAtUtc'>): boolean {
  return room.status === 'Ended' || room.status === 'Cancelled' || !!room.endedAtUtc;
}

/**
 * CORE-1872 — что репостится у Live-сессии; null — приватная или отменённая
 * (`roomRepostBlock`), репоста нет.
 *
 * Прошедшая сессия — всегда сама сессия (`room`), даже при анонсе: карточка анонса
 * не несёт статуса эфира и ведёт на пост, то есть выглядела бы предстоящим
 * событием, а `room` у сервера живая — «завершена», и тап ведёт на итоги именно
 * этой встречи. Второго источника это не создаёт: ссылка на ту же сессию.
 * Предстоящая или идущая — её анонс, когда он есть (CORE-1865).
 */
export function repostRequestForRoom(room: RoomForRepost): { target: RepostTarget; preview: RepostPreview } | null {
  if (roomRepostBlock(room)) return null;
  const target = isRoomOver(room) ? repostTargetForRoom(room.id) : repostTargetForLiveRoom(room);
  const author = resolveRoomAuthor(room);
  return {
    target,
    preview: {
      kind: target.kind,
      title: room.title || room.videoPracticeTitle || null,
      authorName: author.name || null,
      authorPhotoUrl: author.avatarUrl ?? null,
      coverUrl: room.coverImageUrl ?? room.videoPracticeCoverImageUrl ?? null,
      startsAtUtc: room.startedAtUtc ?? room.scheduledAtUtc ?? null,
    },
  };
}

/**
 * Превью оригинала в окне репоста. Строится на стороне, где нажали кнопку:
 * у каждого экрана свой тип карточки, а окну нужны только эти поля.
 */
export interface RepostPreview {
  kind: RepostKind;
  title?: string | null;
  /** Текст или описание — показывается сокращённо. */
  text?: string | null;
  authorName?: string | null;
  /** Сырой путь, как отдаёт сервер; окно прогоняет через `getFileUrl`. */
  authorPhotoUrl?: string | null;
  /** Сырой путь обложки или первой фотографии. */
  coverUrl?: string | null;
  startsAtUtc?: string | null;
}

export function previewFromPost(post: MasterPost): RepostPreview {
  const target = repostTargetForPost(post);
  return {
    kind: target.kind,
    title: post.title ?? null,
    text: post.content ?? null,
    authorName: post.masterName ?? null,
    authorPhotoUrl: post.masterPhotoUrl ?? null,
    coverUrl:
      post.announcement?.coverImageUrl
      ?? post.mediaPreviewUrls?.[0]
      ?? post.mediaUrls?.[0]
      ?? post.imageUrl
      ?? post.posterUrl
      ?? null,
    startsAtUtc: post.eventStartsAtUtc ?? null,
  };
}

/** Превью практики или записи из карточки каталога, библиотеки или экрана детали. */
export function previewFromPractice(
  item: {
    title?: string | null;
    description?: string | null;
    coverImageUrl?: string | null;
    master?: { name?: string | null; photoUrl?: string | null } | null;
    masterName?: string | null;
  },
  kind: 'practice' | 'recording' = 'practice',
): RepostPreview {
  return {
    kind,
    title: item.title ?? null,
    text: item.description ?? null,
    authorName: item.master?.name ?? item.masterName ?? null,
    authorPhotoUrl: item.master?.photoUrl ?? null,
    coverUrl: item.coverImageUrl ?? null,
  };
}

export type RepostFailure = 'not_allowed' | 'not_found' | 'auth_required' | 'error';

const num = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Ошибка создания репоста → что сказать человеку.
 *
 * 403 `repost_not_allowed` — приватное событие, эфир или практика: наружу их
 * не репостят. 400 «... not found» — оригинала нет или он не виден автору.
 */
export function classifyRepostError(err: unknown): RepostFailure {
  const e = (err ?? {}) as Record<string, unknown>;
  const status = num(e.status);
  const code = String(e.code ?? e.Code ?? '').toLowerCase();
  const message = String(e.message ?? e.Message ?? e.error ?? '').toLowerCase();
  if (code === 'repost_not_allowed' || (status === 403 && message.includes('repost'))) return 'not_allowed';
  if (status === 404 || message.includes('not found')) return 'not_found';
  if (status === 401) return 'auth_required';
  if (status === 403) return 'not_allowed';
  return 'error';
}

export interface PublishRepostOptions {
  target: RepostTarget;
  /** Собственный комментарий; пустая строка — репост без слов. */
  content: string;
  /** Мастер публикует в ленту мастера, остальные — в свою ленту пользователя. */
  asMaster: boolean;
  /** CORE-1872 — ключ намерения: один и тот же во всех повторах одной публикации. */
  idempotencyKey?: string;
}

/**
 * Опубликовать репост и сообщить лентам, что появилась новая публикация.
 * Ошибку не глотает — окну нужно её показать.
 */
export async function publishRepost({ target, content, asMaster, idempotencyKey }: PublishRepostOptions): Promise<MasterPost> {
  const text = content.trim();
  const post = asMaster
    ? await api.createPost({ content: text, repostOf: target, idempotencyKey })
    : await api.createCirclePost({ content: text, repostOf: target, idempotencyKey });
  DeviceEventEmitter.emit(FEED_POST_CREATED_EVENT, post);
  return post;
}

/**
 * CORE-1872 — ключ намерения «опубликовать этот текст». Повтор после ошибки с
 * тем же текстом — то же намерение, тот же ключ: если первый POST на сервере
 * прошёл, а ответ потерялся, сервер вернёт уже созданный пост. Изменённый
 * текст — новое намерение и новый ключ, иначе сервер отдал бы старый пост со
 * старым текстом.
 */
export interface RepostIntent {
  key: string;
  text: string;
}

export function repostIntentFor(prev: RepostIntent | null, content: string): RepostIntent {
  const text = content.trim();
  return prev && prev.text === text ? prev : { key: newIdempotencyKey(), text };
}

/** Сколько минут в карточке оригинала: сервер отдаёт секунды. */
export function repostDurationMinutes(src: Pick<RepostSource, 'durationSeconds'>): number | null {
  const s = src.durationSeconds;
  if (s == null || !Number.isFinite(s) || s <= 0) return null;
  return Math.max(1, Math.round(s / 60));
}

/** Статус события в карточке оригинала: отмена важнее остального. */
export type RepostEventState = 'cancelled' | 'live' | 'ended' | 'upcoming' | null;

export function repostEventState(src: Pick<RepostSource, 'kind' | 'status' | 'isCancelled' | 'startsAtUtc' | 'endsAtUtc'>, now = Date.now()): RepostEventState {
  if (src.kind !== 'event' && src.kind !== 'announcement' && src.kind !== 'room') return null;
  if (src.isCancelled || src.status === 'cancelled') return 'cancelled';
  if (src.status === 'live') return 'live';
  if (src.status === 'ended') return 'ended';
  if (src.status === 'upcoming') return 'upcoming';
  const end = Date.parse(src.endsAtUtc ?? '');
  const start = Date.parse(src.startsAtUtc ?? '');
  if (Number.isFinite(end) && end < now) return 'ended';
  if (Number.isFinite(start)) return start > now ? 'upcoming' : 'ended';
  return null;
}

/**
 * Куда ведёт тап по оригиналу. Маршруты — те же, что у карточек этих
 * сущностей; доступ при переходе проверяют сами экраны (закрытый пост —
 * CORE-1692, платная программа — её страница).
 *
 * Возвращает имя маршрута и параметры в терминах `RootStackParamList`;
 * `null` — открывать нечего (оригинал недоступен или без ссылки).
 */
export interface RepostOriginalRoute {
  name: 'PublicUserPost' | 'LiveDetail' | 'EventDetail' | 'Lives' | 'PracticeDetail' | 'RoomAnnouncement' | 'LiveRoomSession' | 'MasterDetail' | 'ProgramPage';
  params: Record<string, unknown>;
}

export function repostOriginalRoute(
  src: RepostSource | null | undefined,
  platform: 'web' | 'native',
): RepostOriginalRoute | null {
  if (!src || !src.id) return null;
  const kind = toRepostKind(src.kind);
  switch (kind) {
    case 'post':
    case 'announcement':
      if (src.userId) return { name: 'PublicUserPost', params: { userId: src.userId, postId: src.id } };
      if (src.watchPartyId != null) return { name: 'RoomAnnouncement', params: { roomId: src.watchPartyId } };
      if (src.liveId != null) return liveRoute(src.liveId, platform);
      if (src.masterEventId != null) return { name: 'Lives', params: { eventId: src.masterEventId } };
      if (src.videoPracticeId != null) return { name: 'PracticeDetail', params: { id: src.videoPracticeId, type: 'practice' } };
      if (src.masterProfileId) return { name: 'MasterDetail', params: { masterId: src.masterProfileId } };
      return null;
    case 'event':
      if (src.eventSource === 'master') {
        // Офлайн-событие мастера живёт в календаре (`/lives?eventId=`), как и
        // ссылка «Поделиться» на него.
        return { name: 'Lives', params: { eventId: src.id } };
      }
      return liveRoute(src.id, platform);
    case 'practice':
      return { name: 'PracticeDetail', params: { id: src.id, type: practiceType(src.type) } };
    case 'recording':
      return liveRoute(src.id, platform);
    case 'room': {
      // CORE-1844 — живая сессия открывается своей страницей `/room/{code}`,
      // завершённая или отменённая — итогами `/practice-rooms/session/{code}`.
      const code = (src.inviteCode ?? '').trim() || String(src.watchPartyId ?? src.id);
      const state = repostEventState(src);
      return state === 'ended' || state === 'cancelled'
        ? { name: 'LiveRoomSession', params: { roomId: code } }
        : { name: 'RoomAnnouncement', params: { roomId: code } };
    }
    default:
      return null;
  }
}

function liveRoute(id: number, platform: 'web' | 'native'): RepostOriginalRoute {
  // Веб показывает эфир и его запись на LiveDetail; на устройстве тот же
  // экран — EventDetail (см. `openFeedActivity`).
  return platform === 'web' ? { name: 'LiveDetail', params: { id } } : { name: 'EventDetail', params: { id } };
}

function practiceType(raw: string | null | undefined): 'video' | 'practice' | 'audio' {
  return raw === 'video' || raw === 'audio' ? raw : 'practice';
}

/** Куда ведёт призыв «Подписаться» / «Открыть программу» у закрытого оригинала. */
export function repostAccessRoute(src: RepostSource): RepostOriginalRoute | null {
  if (src.accessRequired === 'purchase' && src.accessProgramId != null) {
    return { name: 'ProgramPage', params: { programId: src.accessProgramId } };
  }
  if (src.masterProfileId) return { name: 'MasterDetail', params: { masterId: src.masterProfileId } };
  return null;
}
